import React from 'react'
import { Dropzone } from 'Components/Form/Dropzone'
import CloseIcon from 'Assets/Images/icons/modalClose'
import { LinkExternal } from 'Components/LinkExternal'
import styled from 'styled-components'
import { Button, CircularProgress } from '@material-ui/core'
import { Modal } from 'Components/Modal'
import { theme } from '../../../../../../Assets/Styles/theme'

const Container = styled.div`
  padding: 20px;
  font-family: 'Proxima Nova', sans-serif;
  line-height: 1.6;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const BodyText = styled.p`
  font-size: 16px;
  color: #4a5568;
  margin-bottom: 0;
  line-height: 1.6;
  direction: ltr;
  padding-left: 14px;
`

const MainRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  margin-top: 16px;
  margin-bottom: 16px;
`

const LeftSection = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 300px;
  overflow-y: scroll !important;
  scrollbar-width: auto; /* Firefox */
  -webkit-overflow-scrolling: touch; /* iOS */
  direction: rtl;

  /* Force scrollbar to always be visible in WebKit browsers */
  &::-webkit-scrollbar {
    width: 8px;
    background-color: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #c1c1c1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #a8a8a8;
  }
`

const RightSection = styled.div`
  flex: 1;
  justify-content: center;
  align-items: center;
  display: flex;
  gap: 10px;
  flex-direction: column;
`

const ErrorRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  border-bottom: 1px solid #e2e8f0;
  padding: 10px;
`

const ErrorRowItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ErrorContainer = styled.div`
  max-height: 200px;
  overflow-y: scroll !important;
  scrollbar-width: auto; /* Firefox */
  -webkit-overflow-scrolling: touch; /* iOS */

  /* Force scrollbar to always be visible in WebKit browsers */
  &::-webkit-scrollbar {
    width: 8px;
    background-color: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #c1c1c1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #a8a8a8;
  }
`

const RowNumber = styled.div`
  min-width: 70px;
`

const ErrorMessage = styled.div`
  color: red;
  font-size: 14px;
`

const ModalHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

const ModalTitle = styled.div`
  font-size: 24px;
  font-weight: bold;
`
const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: 30px;
`

const StyledCancelButton = styled(Button)`
  && {
    border: 1px solid ${theme.colors.primary};
    color: ${theme.colors.primary};
    background-color: transparent;

    &:hover {
      background-color: ${theme.colors.primary};
      color: white;
    }

    &:disabled {
      border: 1px solid rgba(0, 0, 0, 0.12);
      color: rgba(0, 0, 0, 0.26);
      background-color: transparent;
    }
  }
`

const StyledImportButton = styled(Button)`
  && {
    background-color: ${theme.colors.primary};
    color: white;
    border: 1px solid ${theme.colors.primary};

    &:hover {
      background-color: ${theme.colors.primary};
      opacity: 0.9;
    }

    &:disabled {
      background-color: rgba(0, 0, 0, 0.12);
      color: rgba(0, 0, 0, 0.26);
      border: 1px solid rgba(0, 0, 0, 0.12);
    }
  }
`

const H2HLocationImportModal = ({
  onChange,
  validationErrors,
  handleCancel,
  handleConfirm,
  modalShow,
  isImporting,
}) => {
  const groupedValidationErrorsByRow = validationErrors.reduce((acc, error) => {
    const row = error.split(':')[0]
    if (!acc[row]) {
      acc[row] = []
    }
    acc[row].push(error)
    return acc
  }, {})
  return (
    <Modal
      show={modalShow}
      onHide={isImporting ? () => {} : handleCancel}
      backdrop={isImporting ? 'static' : true}
      keyboard={!isImporting}
    >
      <Container>
        <ModalHeader>
          <ModalTitle>Import Multiple Locations</ModalTitle>
          {isImporting ? (
            <CircularProgress size={20} />
          ) : (
            <div style={{ cursor: 'pointer' }} onClick={handleCancel}>
              <CloseIcon />
            </div>
          )}
        </ModalHeader>
        <MainRow>
          <LeftSection>
            <BodyText>
              Quickly add multiple locations by uploading a CSV file. Here's
              how:
              <br />
              <br />
              <strong>Download Our Template</strong>
              <br />
              First, download the official template to ensure your data is
              formatted correctly.
              <br />
              <br />
              <strong>Fill in Your Location Details</strong>
              <br />
              Open the template and add a new row for each location you want to
              import.
              <br />
              <br />
              <strong>For All Locations:</strong>
              <br />• Provide the basic info like{' '}
              <strong>name, label, type, is_sheltered and needs_permit.</strong>
              <br />• <strong>Important:</strong> You must include the{' '}
              <strong>latitude </strong>
              and <strong>longitude </strong> for each location for accurate
              mapping.
              <br />
              <br />
              <strong>If you're adding a Permanent Location:</strong>
              <br />• In the <strong>is_temporary_event</strong> column, enter{' '}
              <strong>FALSE</strong>.
              <br />• Set its general weekly schedule by entering{' '}
              <strong>TRUE</strong> in the{' '}
              <strong>morning, lunch, or afternoon</strong> columns, and then
              entering <strong>TRUE</strong> for each day that schedule applies
              to (e.g., monday, tuesday, etc.).
              <br />
              <br />
              <strong>If you're adding a Temporary Event:</strong>
              <br />• In the <strong>is_temporary_event</strong> column, enter{' '}
              <strong>TRUE</strong>.
              <br />• You must also provide the <strong>event_type</strong>, a
              short <strong>description</strong>, and the{' '}
              <strong>event_start_date</strong> and{' '}
              <strong>event_end_date</strong> (in YYYY-MM-DD format).
              <br />• Set the event's hours using the{' '}
              <strong>event_opening_hour_1</strong> and
              <strong> event_closing_hour_1</strong> columns (in 24-hour HH:MM
              format). You can add up to two extra time blocks for breaks using
              the other hour columns.
              <br />
              <br />
              <strong>Upload Your File</strong>
              <br />
              Save your completed file in CSV format and upload it here.
              <br />
              <br />
              <strong>Good to Know:</strong> This tool is for setting basic
              schedules. After a successful import, you can always edit a
              location in the platform to set more advanced options, like
              specific hourly schedules for permanent locations. For best
              performance, the import is limited to a maximum of 300 locations
              per file.
            </BodyText>
          </LeftSection>

          <RightSection>
            <Dropzone
              input={{
                onChange,
              }}
              meta={{ touched: false, error: null }}
            />
            <LinkExternal
              href="https://s3-ap-southeast-2.amazonaws.com/prod-oppizi-cdn/location-import-example.csv"
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Template
            </LinkExternal>
          </RightSection>
        </MainRow>

        <ErrorContainer>
          {Object.keys(groupedValidationErrorsByRow).map(row => (
            <ErrorRow key={row}>
              <RowNumber>{row}</RowNumber>
              <ErrorRowItem>
                {groupedValidationErrorsByRow[row].map(error => (
                  <ErrorMessage key={error}>
                    {error.replace(`${row}:`, '')}
                  </ErrorMessage>
                ))}
              </ErrorRowItem>
            </ErrorRow>
          ))}
        </ErrorContainer>
        <ButtonContainer>
          <StyledCancelButton
            onClick={handleCancel}
            variant="outlined"
            disabled={isImporting}
          >
            Cancel
          </StyledCancelButton>
          <StyledImportButton
            onClick={handleConfirm}
            variant="contained"
            disabled={isImporting}
          >
            Import
          </StyledImportButton>
        </ButtonContainer>
      </Container>
    </Modal>
  )
}

export default H2HLocationImportModal
